import { z } from 'zod'

import {
    cents,
    dateDay,
    defineFilters,
    enumOne,
    enumOrAny,
    objectId,
    text,
    type ApiValue,
    type FilterField,
    type FilterOption
} from '@/components/kit/filters'
import { GameType } from '@/types/card'
import { WithdrawalSource, WithdrawalStatus } from '@/types/withdrawal'

export const STATUS_FILTER_OPTIONS: FilterOption<WithdrawalStatus>[] = [
    { value: WithdrawalStatus.PENDING_PAYMENT, label: 'Awaiting payment', hint: 'Stripe has not cleared the fee' },
    { value: WithdrawalStatus.CREATED, label: 'Created', hint: 'Requested and not yet actioned' },
    { value: WithdrawalStatus.ORDERED, label: 'Ordered', hint: 'Stock ordered from the supplier' },
    { value: WithdrawalStatus.PENDING, label: 'Pending', hint: 'Waiting on stock' },
    { value: WithdrawalStatus.PACKED, label: 'Packed', hint: 'Boxed by the warehouse' },
    { value: WithdrawalStatus.SHIPPING, label: 'Shipping', hint: 'Handed to the carrier' },
    { value: WithdrawalStatus.COMPLETED, label: 'Completed' },
    { value: WithdrawalStatus.RETURNED, label: 'Returned', hint: 'The carrier sent the parcel back' },
    { value: WithdrawalStatus.CANCELLED, label: 'Cancelled' },
    { value: WithdrawalStatus.REFUNDED, label: 'Refunded' }
]

export const GAME_FILTER_OPTIONS: FilterOption<GameType>[] = [
    { value: GameType.POKEMON, label: 'Pok\u00e9mon' },
    { value: GameType.MAGIC, label: 'Magic: The Gathering' },
    { value: GameType.ONE_PIECE, label: 'One Piece' },
    { value: GameType.RIFTBOUND, label: 'Riftbound' }
]

export const SOURCE_FILTER_OPTIONS: FilterOption<WithdrawalSource>[] = [
    { value: WithdrawalSource.BACKPACK, label: 'Backpack', hint: 'Shipping cards the user already owned' },
    { value: WithdrawalSource.MARKET, label: 'Market', hint: 'Bought from the catalogue and shipped' }
]

const ORDER_OPTIONS: FilterOption<'asc' | 'desc'>[] = [
    { value: 'asc', label: 'Oldest first', hint: 'FIFO, which is the order a queue should be worked in' },
    { value: 'desc', label: 'Newest first' }
]

export interface WithdrawalQueue {
    id: string
    label: string
    /** Empty means every status. */
    statuses: WithdrawalStatus[]
    /** One line under the table header explaining what the operator is looking at. */
    description: string
    /**
     * How long an order may sit here, counted from the request. Only the queues this
     * console works carry one; the rest wait on Stripe, the warehouse, or nobody.
     */
    slaHours?: number
}

/**
 * The work queues, in the order the flow runs.
 *
 * This is the replacement for a column per status. Ten columns put six dead ends on
 * screen permanently -- `pending_payment` waits on Stripe, `packed` belongs to the
 * warehouse tooling, and four are terminal -- while the three statuses that actually
 * carry work sat in the same visual weight as the rest.
 */
export const WITHDRAWAL_QUEUES: WithdrawalQueue[] = [
    {
        id: 'inbox',
        label: 'Inbox',
        statuses: [WithdrawalStatus.CREATED],
        description: 'Requested and not yet actioned. Work these oldest first.',
        slaHours: 24
    },
    {
        id: 'sourcing',
        label: 'Sourcing',
        statuses: [WithdrawalStatus.ORDERED, WithdrawalStatus.PENDING],
        description: 'Stock is on order or on backorder. Nothing ships until it lands.',
        slaHours: 7 * 24
    },
    {
        id: 'packed',
        label: 'Packed',
        statuses: [WithdrawalStatus.PACKED],
        description: 'Boxed and waiting on a label. The warehouse tooling moves these, not this console.'
    },
    {
        id: 'transit',
        label: 'In transit',
        statuses: [WithdrawalStatus.SHIPPING],
        description: 'With the carrier. Without a date filter this shows the last 90 days only.'
    },
    {
        id: 'exceptions',
        label: 'Exceptions',
        statuses: [WithdrawalStatus.PENDING_PAYMENT, WithdrawalStatus.RETURNED],
        description: 'Waiting on a Stripe payment, or returned to sender. Both need someone to look.'
        // ponytail: no SLA, because a returned order has already been through transit, so a
        // clock started at the request reads it as late on the day it arrives. Give this queue
        // one once the list can sort and filter on when the order entered its status.
    },
    {
        id: 'closed',
        label: 'Closed',
        statuses: [WithdrawalStatus.COMPLETED, WithdrawalStatus.CANCELLED, WithdrawalStatus.REFUNDED],
        description: 'Delivered, cancelled, or refunded. Nothing here is actionable.'
    },
    {
        id: 'all',
        label: 'All',
        statuses: [],
        description: 'Every shipping order, whatever state it is in.'
    }
]

const INBOX_STATUSES = WITHDRAWAL_QUEUES[0]!.statuses

function sameStatusSet(a: WithdrawalStatus[], b: WithdrawalStatus[]): boolean {
    if (a.length !== b.length) return false
    const set = new Set(a)
    return b.every(status => set.has(status))
}

/** The queue whose status set matches, or null when the operator picked a custom mix. */
export function activeQueue(statuses: WithdrawalStatus[]): WithdrawalQueue | null {
    return WITHDRAWAL_QUEUES.find(queue => sameStatusSet(queue.statuses, statuses)) ?? null
}

/**
 * Status is a queue rather than a plain multi-select default, so the page opens on
 * the Inbox instead of on ten thousand mostly-closed orders. An empty array is a
 * real value here -- it is the All queue -- so it cannot double as "unset".
 */
const statuses: FilterField<WithdrawalStatus[]> = {
    kind: 'multiEnum',
    default: INBOX_STATUSES,
    schema: z.array(z.nativeEnum(WithdrawalStatus)).catch(INBOX_STATUSES),
    // The URL reads better as a plural; the endpoint's field is `status`, which
    // takes either one value or a set.
    apiKey: 'status',
    label: 'Status',
    options: STATUS_FILTER_OPTIONS as FilterOption[],
    // The queue control above the table already names the default, so it does not
    // also need a chip repeating it.
    isEmpty: value => sameStatusSet(value, INBOX_STATUSES),
    toApi: value => (value.length === 0 ? undefined : [...value]),
    describe: value =>
        value.map(entry => STATUS_FILTER_OPTIONS.find(option => option.value === entry)?.label ?? entry).join(', ') ||
        'Any status'
}

/**
 * Any / yes / no over a boolean the API expects as a real boolean. The shared
 * `triState` field sends the strings `'true'` and `'false'`, which this endpoint
 * rejects with a 400.
 */
function booleanTriState({ label, apiKey }: { label: string; apiKey?: string }): FilterField<'' | 'true' | 'false'> {
    return {
        kind: 'tri',
        default: '',
        schema: z.enum(['', 'true', 'false']).catch(''),
        apiKey,
        label,
        isEmpty: value => value === '',
        toApi: (value): ApiValue | undefined => (value === '' ? undefined : value === 'true'),
        describe: value => (value === 'true' ? 'Yes' : 'No')
    }
}

/** What the endpoint accepts for this list, which is higher than the other lists. */
export const WITHDRAWAL_MAX_PAGE_SIZE = 1000

/**
 * Page sizes offered in the footer.
 *
 * The large ones exist because sweeping a whole queue is a real task here: selecting
 * everything in Sourcing to move it, or exporting exactly what is on screen. Orders
 * embed their cards, so 1000 rows is a heavy request and stays an explicit choice.
 */
export const WITHDRAWAL_PAGE_SIZES = [25, 50, 100, 250, 500, WITHDRAWAL_MAX_PAGE_SIZE]

/**
 * A page past this size stops warming its neighbours and starts virtualizing rows.
 * Prefetching two more thousand-row pages costs far more than the paging it saves.
 */
export const WITHDRAWAL_LARGE_PAGE = 100

/**
 * Every filter is now the server's job. The board filtered amounts in the browser
 * after fetching, so a column header counted rows the operator could not see and
 * "load more" pulled pages that vanished on arrival.
 */
export const withdrawalFilters = defineFilters(
    {
        search: text({ label: 'Search' }),
        user: objectId({ label: 'User' }),
        statuses: statuses as FilterField<WithdrawalStatus[]>,
        game: enumOrAny(GAME_FILTER_OPTIONS, { label: 'Game' }),
        source: enumOrAny(SOURCE_FILTER_OPTIONS, { label: 'Source' }),
        priority: booleanTriState({ label: 'Priority', apiKey: 'isPriority' }),
        dateFrom: dateDay({ label: 'From', apiKey: 'createdAtFrom', boundary: 'start' }),
        dateTo: dateDay({ label: 'To', apiKey: 'createdAtTo', boundary: 'end' }),
        amountMin: cents({ label: 'Min amount' }),
        amountMax: cents({ label: 'Max amount' }),
        sortOrder: enumOne(ORDER_OPTIONS, 'asc', { label: 'Order' })
    },
    { defaultLimit: 50, maxLimit: WITHDRAWAL_MAX_PAGE_SIZE }
)

export type WithdrawalSearch = ReturnType<typeof withdrawalFilters.parse>

/** Wire params, plus the `sortBy` the endpoint requires alongside `sortOrder`. */
export function withdrawalApiParams(search: WithdrawalSearch): Record<string, ApiValue> {
    return { ...withdrawalFilters.toApiParams(search), sortBy: 'createdAt' }
}

/** The queue an order's status belongs to, for the "moved out of this queue" copy. */
export function queueForStatus(status: WithdrawalStatus): WithdrawalQueue | undefined {
    return WITHDRAWAL_QUEUES.find(queue => queue.statuses.includes(status))
}
